import type { ColumnsType, ColumnType } from "@similarweb/pro-logic-components";
import { findAndSetColumnSortOrder, StateChangeCell } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import { CompareBarCell } from "app/components/React/Table/cells/CompareBarCell";
import type { ITableCellProps } from "app/components/React/Table/interfaces/ITableCellProps";
import {
    NO_DATA_SIGN,
    THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE,
    THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE,
} from "app/filters/const";
import type { ISelectedSerp } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/shared/KeywordsReportTypes";
import { ESerpGroups, ESerpTypes } from "app/common/serp/SerpFilterMaps";
import { TrafficShareWithVisits, UrlCellWebsiteAnalysis, WaKeywordPosition } from "app/components/React/Table/cells";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { SerpTableCellSingle } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTableCellSingle";

import type { ITableOptions, TransformedDataItem, TransformedResponseData } from "./types";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";

const tableOptions: ITableOptions = {
    sitesForSerpColumn: [],
    onSerpIconClick: (serp: ISelectedSerp) => {
        proNavigator.applyUpdateParams(
            {
                serpFilterGroup: ESerpGroups.featuresOnSerp,
                serpFilterTypes: ESerpTypes[serp.id],
            },
            { reload: true },
        );
    },
    serpFeatureItemBackgroundColor: "",
};

export const getColumns = (sourceType: string, country: string | number, sortConfig: { sort: string; asc: boolean }) =>
    [
        {
            dataIndex: "Url",
            title: translate("keywords.by.website.table.columns.expanded_row.top_url"),
            tooltipSubtitle: translate("keywords.by.website.table.columns.expanded_row.top_url.tooltip"),
            render: (rowCellData: string) => <UrlCellWebsiteAnalysis value={rowCellData} />,
            width: 275,
            align: "left",
            fixed: "left",
        },
        {
            dataIndex: "clicksShare",
            title: translate("keywords.by.website.table.columns.expanded_row.clicks.title"),
            tooltipSubtitle: translate("keywords.by.website.table.columns.expanded_row.clicks.tooltip"),
            render: (rowCellData: { totalClicks: number; value: number }) => (
                <TrafficShareWithVisits
                    row={rowCellData}
                    value={rowCellData.value}
                    applyAbbrNumberFilter
                    undefinedSign={NO_DATA_SIGN}
                    totalVisitsRowProp="totalClicks"
                    thresholdForTotalFilter={THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE}
                    thresholdTrafficShareWithVisitsPercentage={THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE}
                    displayLessThanThreshold={true}
                />
            ),
            width: 95,
            sorter: true,
        },
        {
            dataIndex: "clicksChange",
            title: translate("keywords.by.website.table.columns.expanded_row.change.title"),
            tooltipSubtitle: translate("keywords.by.website.table.columns.expanded_row.change.tooltip"),
            width: 95,
            render: (rowCellData: TransformedDataItem["clicksChange"]) => {
                return (
                    <StateChangeCell
                        value={rowCellData}
                        formattedValue={rowCellData.changeValueFormatted}
                    />
                );
            },
            align: "right",
            sorter: true,
        },
        ...(sourceType == EKeywordTabs[EKeywordTabs.Total]
            ? [
                  {
                      dataIndex: "OrganicShare",
                      title: translate("keywords.by.website.table.columns.expanded_row.sourceType.share.title"),
                      tooltipSubtitle: translate("keywords.by.website.table.columns.expanded_row.sourceType.tooltip"),
                      width: 88,
                      align: "left",
                      fixed: "right",
                      render: (rowCellData: number) => (
                          <CompareBarCell
                              isSourceType={true}
                              value={rowCellData}
                              shouldAddRightSpace={true}
                          />
                      ),
                  },
              ]
            : []),

        ...(country !== "999" && sourceType !== EKeywordTabs[EKeywordTabs.Paid]
            ? [
                  {
                      dataIndex: "position",
                      title: translate("keywords.by.website.table.columns.expanded_row.position.title"),

                      tooltipSubtitle: translate(
                          `keywords.by.website.table.columns.expanded_row.position.${sourceType?.toLowerCase()}.tooltip`,
                      ),
                      width: 75,
                      align: "right",

                      render: (rowCellData: { position: { Value: number }[] }) => {
                          return (
                              <WaKeywordPosition
                                  field={"position"}
                                  row={rowCellData}
                                  {...(rowCellData as unknown as ITableCellProps)}
                              />
                          );
                      },
                  },
              ]
            : []),
        {
            dataIndex: "SiteSerpFeatures",
            title: translate("serp.column.name"),
            tooltipSubtitle: translate(`keywords.by.website.table.columns.expanded_row.serp.tootltip`),
            width: 100,
            align: "left",

            render: (_, otherCellsRowData) => {
                tableOptions.sitesForSerpColumn = [{ name: otherCellsRowData.Url }];
                tableOptions.serpFeatureItemBackgroundColor = colorsPalettes.sky["200"];

                return (
                    <SerpTableCellSingle
                        row={otherCellsRowData}
                        tableOptions={tableOptions}
                    />
                );
            },
        },
    ].map((col) =>
        findAndSetColumnSortOrder(
            col as ColumnType<TransformedResponseData["data"][number]>,
            sortConfig.sort,
            sortConfig.asc,
        ),
    ) as ColumnsType<TransformedResponseData["data"][number]>;
